import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Dialog, Typography } from '@zendesk/forethought-elements';
import { useArchiveWorkflowMutation } from 'src/services/orchestrator/orchestratorApi';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { useAppDispatch } from 'src/store/hooks';

interface DeleteAutomationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  workflowId: string;
}

export const DeleteAutomationDialog = ({
  isOpen,
  onClose,
  onSuccess,
  workflowId,
}: DeleteAutomationDialogProps) => {
  const { t } = useTranslation('orchestrator');
  const dispatch = useAppDispatch();
  const [isDeleting, setIsDeleting] = useState(false);
  const [archiveWorkflow] = useArchiveWorkflowMutation();

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await archiveWorkflow(workflowId).unwrap();
      onSuccess?.();
      dispatch(
        setGlobalToastOptions({
          autoHideDuration: 3000,
          title: t('deleteAutomationDialog.toastDeleteSuccess'),
          variant: 'main',
        }),
      );
    } catch {
      dispatch(
        setGlobalToastOptions({
          autoHideDuration: 3000,
          title: t('deleteAutomationDialog.toastDeleteError'),
          variant: 'danger',
        }),
      );
    }

    setIsDeleting(false);
    onClose();
  };

  return (
    <Dialog
      footer={
        <>
          <Button onClick={onClose} variant='ghost'>
            {t('addJsonDrawer.cancel')}
          </Button>
          <Button
            aria-label={t('deleteAutomationDialog.deleteConfirmAriaLabel')}
            disabled={isDeleting}
            isLoading={isDeleting}
            onClick={handleDelete}
            variant='danger'
          >
            {t('conditionNode.deleteConfirm')}
          </Button>
        </>
      }
      hideCloseButton
      onClose={onClose}
      open={isOpen}
      title={t('deleteAutomationDialog.title')}
    >
      <Typography variant='font14'>
        {t('deleteAutomationDialog.body')}
        <br />
        {t('deleteAutomationDialog.bodyLine2')}
      </Typography>
    </Dialog>
  );
};
